// Event schema v1 and v2. See docs/spec/events.md §2. Hand-written validation keeps the kiosk
// bundle small; zod would add ~50 KB to every surface for a dozen field checks.
//   v1: the core event types below, a closed list. Every event recorded before modules is v1, and
//       core events stay v1, so their bytes (and hashes) never change.
//   v2: the same fields; `type` may also be a module's namespaced type ("transport:board") that
//       the module registered (registerModuleEvents). A verifier knows a v2 type only if it ships
//       the module's manifest; an unknown one is reported, never guessed.
import { isHex64 } from "./bytes.ts";
import type { ModuleName } from "./roles.ts";

export const SCHEMA_VERSION = 1 as const;
export const MODULE_SCHEMA_VERSION = 2 as const;

export const EVENT_TYPES = [
  "pickup", "dropoff", "transfer", "absence", "override", "correction", "note", "shift_start", "shift_end",
  // A parent asking at the self-service kiosk to collect a child; no staff member is present.
  // The staff member who hands the child over seals the pickup, which names it in data.request_id.
  "pickup_request",
  // A parent saying they have brought a child (at the kiosk, through a door code, or from their
  // phone without one). The child's teacher confirms by sealing the dropoff that names it.
  "dropoff_request",
] as const;
export type EventType = (typeof EVENT_TYPES)[number];

// "app": sent from the guardian app signed by its registered key, without a door code.
export const AUTH_METHODS = ["tile", "pin", "qr", "code", "id_check", "none", "door_qr", "app"] as const;
export type AuthMethod = (typeof AUTH_METHODS)[number];

export const FLAGS = [
  "restriction_ack", "cannot_sign", "unlisted_adult", "duplicate_suspect", "typed_name", "late", "offline",
  // A request whose adult gave no PIN, pass or code: staff check who they are in person.
  "check_id",
  // A child who came in on their own, marked here by their teacher (the school allows it for their grade).
  "walk_in",
  // A pickup whose adult skipped the signature, where the school's rules make it optional.
  "signature_skipped",
  // A child who went home on their own (walk, bike), marked by staff; needs the parent's consent.
  "home_alone",
  // A child collected by an older brother or sister at the school (data.sibling_child_id), with consent.
  "sibling",
  // A pickup without a photo, where the school's rules make the photo optional (or the camera failed).
  "no_photo",
  // Released to an approved adult at the reunification point during an emergency (no signature needed).
  "reunification",
] as const;
export type Flag = (typeof FLAGS)[number];

export const ID_TYPES = ["drivers_license", "state_id", "passport", "military_id", "other"] as const;

/** An event type a module adds (docs/spec/modules.md §2). */
export interface ModuleEventDef {
  type: ModuleName;
  /** Plain words for records, PDFs and screens: "Boarded the bus". */
  label: string;
  child: "required" | "optional" | "none";
  staff: "required" | "optional";
  /** Device kinds that may record it; any when omitted. */
  devices?: ("kiosk" | "phone")[];
  /** The module's own rules for the pre-image (usually its `data`); returns problems. */
  validate?: (pre: Readonly<Record<string, unknown>>) => string[];
  /**
   * True when this event hands the child to an adult (a bus drop with a parent at the stop): the
   * site then checks the adult exactly as for a pickup (authorised or delegated at the time,
   * restrictions, unknown adult) and flags what fails.
   */
  handsOver?: (pre: Readonly<PreImage>) => boolean;
  /** How the event changes the child's presence (state and who has them), or null for no change. */
  presence?: (pre: Readonly<PreImage>) => { state: "in" | "out"; custodian: string | null } | null;
}

const MODULE_EVENTS = new Map<string, ModuleEventDef>();
/** Adds a module's event types; called by installModules at startup in every runtime and verifier. */
export function registerModuleEvents(defs: readonly ModuleEventDef[]) {
  for (const d of defs) {
    if (!/^[a-z][a-z0-9_]*:[a-z][a-z0-9_]*$/.test(d.type)) throw new Error(`module event type ${d.type} must be "module:name"`);
    MODULE_EVENTS.set(d.type, d);
  }
}
export const moduleEvent = (type: string): ModuleEventDef | undefined => MODULE_EVENTS.get(type);
export const isModuleEventType = (type: string) => MODULE_EVENTS.has(type);

export interface FaceBox { conf: number; box: [number, number, number, number] }

export interface PreImage {
  /** 1 for core types; 2 for a module's type (and allowed for core types too). */
  v: 1 | 2;
  id: string;
  site_id: string;
  type: EventType | ModuleName;
  group_id: string | null;
  child_id: string | null;
  adult_id: string | null;
  claimed_name: string | null;
  id_check: { type: (typeof ID_TYPES)[number]; last4: string } | null;
  /** The staff member on duty; null only on a pickup_request, which nobody staffs. */
  staff_id: string | null;
  witness_staff_id: string | null;
  device_id: string;
  device_ts: number;
  device_mono: number;
  photo_sha256: string | null;
  face: FaceBox | null;
  stroke_sha256: string | null;
  attestation: string | null;
  auth_method: AuthMethod;
  flags: Flag[];
  reason: string | null;
  data: Record<string, string | number | boolean | null> | null;
}

export interface SealedBody {
  v: 1;
  seq: number;
  server_ts: number;
  clock_offset_ms: number;
  pre: PreImage;
  device_sig: string;
}

export interface SealedEvent extends SealedBody {
  prev_hash: string;
  body_hash: string;
  event_hash: string;
}

const PRE_KEYS = [
  "v", "id", "site_id", "type", "group_id", "child_id", "adult_id", "claimed_name", "id_check", "staff_id",
  "witness_staff_id", "device_id", "device_ts", "device_mono", "photo_sha256", "face", "stroke_sha256",
  "attestation", "auth_method", "flags", "reason", "data",
] as const;

const ID_RE = /^[A-Za-z0-9_-]{1,64}$/;
const isId = (x: unknown) => typeof x === "string" && ID_RE.test(x);
const isStr = (x: unknown, max: number) => typeof x === "string" && x.length > 0 && x.length <= max;
const isInt = (x: unknown, min = 0) => Number.isSafeInteger(x) && (x as number) >= min;
const nullOr = (x: unknown, ok: (x: unknown) => boolean) => x === null || ok(x);

/** Returns a list of problems; empty means the pre-image is valid (schema v1, or v2 with a registered module type). */
export function validatePreImage(p: unknown): string[] {
  const errs: string[] = [];
  if (typeof p !== "object" || p === null || Array.isArray(p)) return ["pre-image is not an object"];
  const o = p as Record<string, unknown>;
  const keys = Object.keys(o);
  for (const k of PRE_KEYS) if (!(k in o)) errs.push(`missing ${k}`);
  for (const k of keys) if (!(PRE_KEYS as readonly string[]).includes(k)) errs.push(`unknown field ${k}`);
  if (errs.length) return errs;

  const need = (cond: boolean, msg: string) => { if (!cond) errs.push(msg); };
  need(o.v === 1 || o.v === 2, "v must be 1 or 2");
  need(isId(o.id), "id invalid");
  need(isId(o.site_id), "site_id invalid");
  const core = (EVENT_TYPES as readonly unknown[]).includes(o.type);
  const mod = typeof o.type === "string" ? MODULE_EVENTS.get(o.type) : undefined;
  if (!core && !mod) need(false, typeof o.type === "string" && o.type.includes(":") ? `unknown module event type ${o.type} (the verifier needs that module's manifest)` : "type invalid");
  if (mod) need(o.v === 2, `${o.type} is a module event: v must be 2`);
  need(nullOr(o.group_id, isId), "group_id invalid");
  need(nullOr(o.child_id, isId), "child_id invalid");
  need(nullOr(o.adult_id, isId), "adult_id invalid");
  need(nullOr(o.claimed_name, (x) => isStr(x, 120)), "claimed_name invalid");
  need(
    nullOr(o.id_check, (x) => {
      const c = x as Record<string, unknown>;
      return typeof c === "object" && Object.keys(c).length === 2 && (ID_TYPES as readonly unknown[]).includes(c.type)
        && typeof c.last4 === "string" && /^[A-Z0-9]{4}$/.test(c.last4);
    }),
    "id_check invalid",
  );
  if (o.type === "pickup_request" || o.type === "dropoff_request") need(o.staff_id === null && o.witness_staff_id === null, `${o.type} has no staff_id or witness`);
  else if (mod && mod.staff === "optional") need(nullOr(o.staff_id, isId), "staff_id invalid");
  else need(isId(o.staff_id), "staff_id invalid");
  need(nullOr(o.witness_staff_id, isId), "witness_staff_id invalid");
  need(isId(o.device_id), "device_id invalid");
  need(isInt(o.device_ts, 1), "device_ts invalid");
  need(isInt(o.device_mono), "device_mono invalid");
  need(nullOr(o.photo_sha256, isHex64), "photo_sha256 invalid");
  need(
    nullOr(o.face, (x) => {
      const f = x as Record<string, unknown>;
      return typeof f === "object" && Object.keys(f).length === 2 && isInt(f.conf) && (f.conf as number) <= 1000
        && Array.isArray(f.box) && f.box.length === 4 && f.box.every((n) => isInt(n));
    }),
    "face invalid",
  );
  need(nullOr(o.stroke_sha256, isHex64), "stroke_sha256 invalid");
  need(nullOr(o.attestation, (x) => isStr(x, 2000)), "attestation invalid");
  need((AUTH_METHODS as readonly unknown[]).includes(o.auth_method), "auth_method invalid");
  need(
    Array.isArray(o.flags) && o.flags.every((f) => (FLAGS as readonly unknown[]).includes(f))
      && o.flags.every((f, i, a) => i === 0 || (a[i - 1] as string) < (f as string)),
    "flags must be known, sorted and unique",
  );
  need(nullOr(o.reason, (x) => isStr(x, 1000)), "reason invalid");
  need(
    nullOr(o.data, (x) => typeof x === "object" && !Array.isArray(x)
      && Object.values(x as object).every((v) => v === null || ["string", "number", "boolean"].includes(typeof v))),
    "data invalid",
  );
  if (errs.length) return errs;

  // A module's event: its own child rule and checks; none of the core custody rules below.
  if (mod) {
    if (mod.child === "required") need(o.child_id !== null, `${mod.type} needs child_id`);
    if (mod.child === "none") need(o.child_id === null, `${mod.type} has no child_id`);
    if (o.witness_staff_id !== null) need(o.witness_staff_id !== o.staff_id, "witness must be a different staff member");
    for (const problem of mod.validate?.(o) ?? []) errs.push(problem);
    return errs;
  }

  // Cross-field rules from the plan (§5, §6).
  const t = o.type as EventType;
  const custody = t === "pickup" || t === "dropoff" || t === "pickup_request" || t === "dropoff_request";
  if (t !== "shift_start" && t !== "shift_end") need(o.child_id !== null, `${t} needs child_id`);
  // A walk-in: a drop-off with no adult, recorded by the child's teacher. Going home alone or with a
  // sibling: a pickup with no adult and no signature, recorded by staff (the school checks consent).
  const walkIn = t === "dropoff" && (o.flags as string[]).includes("walk_in");
  if (walkIn) need(o.adult_id === null && o.staff_id !== null, "walk_in has no adult and names the teacher");
  const noAdult = t === "pickup" && ((o.flags as string[]).includes("home_alone") || (o.flags as string[]).includes("sibling"));
  if (noAdult) need(o.adult_id === null && o.staff_id !== null, "home_alone and sibling have no adult and name the staff member");
  if (t === "pickup" && (o.flags as string[]).includes("sibling")) need(o.data !== null && isId((o.data as Record<string, unknown>).sibling_child_id), "sibling needs data.sibling_child_id");
  if (custody && !walkIn && !noAdult) {
    need(o.adult_id !== null || o.claimed_name !== null, `${t} needs adult_id or claimed_name`);
    need(o.attestation !== null, `${t} needs the attestation text shown`);
  }
  if (t === "pickup_request") {
    // Signed at the kiosk; the witness for "cannot sign", "typed name" or someone not on the list
    // is the staff member who releases the child, on the pickup that closes the request.
    const f = o.flags as string[];
    need(f.includes("cannot_sign") || f.includes("typed_name") || f.includes("signature_skipped") || o.stroke_sha256 !== null, "pickup_request needs a signature unless flagged cannot_sign, typed_name or signature_skipped");
    if (f.includes("typed_name")) need(o.claimed_name !== null, "typed_name needs the typed name");
  }
  if (t === "dropoff_request" && (o.flags as string[]).includes("typed_name")) need(o.claimed_name !== null, "typed_name needs the typed name");
  if ((t === "pickup" || t === "dropoff") && o.data !== null && "request_id" in (o.data as object)) need(isId((o.data as Record<string, unknown>).request_id), "data.request_id invalid");
  if (t === "pickup" && !noAdult) {
    // A drawn signature, or one of the two witnessed alternatives (plan §6): "cannot sign"
    // (injury, disability) or the accessible typed-name path.
    const cannotSign = (o.flags as string[]).includes("cannot_sign");
    const typedName = (o.flags as string[]).includes("typed_name");
    const skipped = (o.flags as string[]).includes("signature_skipped");
    const reunion = (o.flags as string[]).includes("reunification");
    need(cannotSign || typedName || skipped || reunion || o.stroke_sha256 !== null, "pickup needs a signature unless flagged cannot_sign, typed_name, signature_skipped or reunification");
    if (reunion) need(o.adult_id !== null && o.staff_id !== null, "reunification releases to an approved adult, recorded by staff");
    if (cannotSign) need(o.witness_staff_id !== null, "cannot_sign needs a witness");
    if (typedName) need(o.witness_staff_id !== null && o.claimed_name !== null, "typed_name needs the typed name and a witness");
  }
  if (o.adult_id === null && custody && !walkIn && !noAdult) {
    need(o.id_check !== null || (o.flags as string[]).includes("typed_name"), "someone else needs id_check or typed_name");
    if (t === "pickup") need(o.witness_staff_id !== null, "pickup by an unlisted adult needs a second staff PIN");
  }
  if (o.witness_staff_id !== null) need(o.witness_staff_id !== o.staff_id, "witness must be a different staff member");
  if (t === "override" || t === "correction") need(o.reason !== null, `${t} needs a reason`);
  if (t === "correction") need(o.data !== null && isInt((o.data as Record<string, unknown>).corrects_seq, 1), "correction needs data.corrects_seq");
  if (t === "transfer") need(o.data !== null && typeof (o.data as Record<string, unknown>).to === "string", "transfer needs data.to");
  return errs;
}

export interface Stroke { v: 1; w: number; h: number; strokes: [number, number, number][][] }

export function validateStroke(s: unknown): string[] {
  const o = s as Stroke;
  if (typeof o !== "object" || o === null) return ["stroke is not an object"];
  const errs: string[] = [];
  if (JSON.stringify(Object.keys(o).sort()) !== '["h","strokes","v","w"]') errs.push("stroke fields must be v,w,h,strokes");
  if (o.v !== 1) errs.push("stroke v must be 1");
  if (!isInt(o.w, 1) || !isInt(o.h, 1)) errs.push("stroke w/h invalid");
  if (!Array.isArray(o.strokes) || !o.strokes.every((line) => Array.isArray(line) && line.length > 0
    && line.every((pt) => Array.isArray(pt) && pt.length === 3 && pt.every((n) => Number.isSafeInteger(n)))))
    errs.push("strokes must be arrays of [x,y,t] integers");
  return errs;
}

export const strokePointCount = (s: Stroke) => s.strokes.reduce((n, l) => n + l.length, 0);
